import React from "react";
import { useQuery } from "@tanstack/react-query";
import { SGetKoboRatingDistribution } from "../../../apis/app.service";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  ResponsiveContainer,
} from "recharts";
import { colors } from "../../../theme/colors";
import {
  ChartLoading,
  ChartSyncNotice,
  GRID_STROKE,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  humanize,
  makeSegmentLabel,
  yAxisWidthFor,
} from "./chartHelpers";
import { useSyncPolling } from "./syncState";

// The scale itself, worst to best — this is the fixed row order, never re-sorted.
const RATING_ORDER = ["Very Bad", "Bad", "Okay", "Good", "Very Good"];

// Same categorical palette and folding rule as the Department chart, so the two
// charts side by side read as one visual system. Past eight organizations the
// tail folds into "Other organizations" rather than inventing more hues.
const ORGANIZATION_COLORS = [
  "#2a78d6",
  "#eb6834",
  "#1baf7a",
  "#eda100",
  "#e87ba4",
  "#008300",
  "#4a3aa7",
  "#e34948",
];
const ORGANIZATION_SLOT_LIMIT = 8;
const OTHER_ORGANIZATION_LABEL = "Other organizations";
const OTHER_ORGANIZATION_COLOR = "#7c7b76";

interface RatingRow {
  organization: string;
  totalResponses: number;
  counts: Record<string, number>;
  percentages: Record<string, number>;
  positiveScore: number;
}

interface RatingSummary {
  rating: string;
  totalResponses: number;
  sharePercent: number;
}

interface OrganizationSatisfactionProps {
  /** Kobo project id — the chart only renders for a single selected survey */
  survey?: string;
  organization: string | null;
  startDate: string;
  endDate: string;
  onSyncStatusChange?: (isSyncing: boolean) => void;
}

function RatingTooltip({ active, payload, label }: any) {
  if (!active || !payload || payload.length === 0) return null;

  const row: RatingSummary | undefined = payload[0]?.payload?._row;
  const entries = payload
    .filter((entry: any) => entry.value !== undefined && entry.value !== null)
    .sort((a: any, b: any) => b.value - a.value);

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "0.75rem 0.9rem",
        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
        fontSize: "0.8rem",
      }}
    >
      <div style={{ fontWeight: 600, color: TEXT_PRIMARY }}>{label}</div>
      {row && (
        <div style={{ color: TEXT_SECONDARY, marginBottom: "0.5rem" }}>
          {row.sharePercent}% of all responses •{" "}
          {row.totalResponses.toLocaleString()} responses
        </div>
      )}
      {entries.map((entry: any) => (
        <div
          key={entry.dataKey}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.1rem 0",
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              backgroundColor: entry.color,
              flexShrink: 0,
            }}
          />
          <span style={{ color: TEXT_SECONDARY, flex: 1 }}>{entry.name}</span>
          <span style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>
            {entry.value}%
          </span>
        </div>
      ))}
    </div>
  );
}

function OrganizationSatisfaction({
  survey,
  organization,
  startDate,
  endDate,
  onSyncStatusChange,
}: OrganizationSatisfactionProps) {
  const { refetchInterval, resolve, reset } = useSyncPolling();

  const {
    data: response,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      "koboRatingDistribution",
      startDate,
      endDate,
      organization,
      survey,
    ],
    queryFn: () =>
      SGetKoboRatingDistribution({
        startDate,
        endDate,
        organization: organization || undefined,
        survey,
      }),
    enabled: Boolean(survey),
    refetchInterval,
    refetchOnWindowFocus: false,
  });

  const sync = resolve(response);
  const isSyncing = sync.state === "syncing";
  const syncEnded = sync.state === "failed" || sync.state === "stalled";

  const retrySync = React.useCallback(() => {
    reset();
    refetch();
  }, [reset, refetch]);

  React.useEffect(() => {
    if (onSyncStatusChange) {
      onSyncStatusChange(Boolean(isSyncing));
    }
  }, [onSyncStatusChange, isSyncing]);

  const ratings: string[] = React.useMemo(
    () => response?.data?.ratings || RATING_ORDER,
    [response],
  );

  const allOrganizations: RatingRow[] = React.useMemo(
    () => response?.data?.organizations || [],
    [response],
  );

  // Organizations are stacked and colored in alphabetical order — same convention as
  // the Department chart — so one keeps its hue when the date range shifts the volume
  // ranking. Which organizations survive the fold IS ranked by volume; the display
  // order among survivors is alphabetical, with the folded bucket always last.
  const { kept, folded, organizationLabels } = React.useMemo(() => {
    const ranked = [...allOrganizations].sort(
      (a, b) => b.totalResponses - a.totalResponses,
    );
    const needsFolding = ranked.length > ORGANIZATION_SLOT_LIMIT;
    const keptRows = needsFolding
      ? ranked.slice(0, ORGANIZATION_SLOT_LIMIT - 1)
      : ranked;
    const foldedRows = needsFolding
      ? ranked.slice(ORGANIZATION_SLOT_LIMIT - 1)
      : [];

    const namedLabels = keptRows
      .map((row) => row.organization)
      .sort((a, b) => humanize(a).localeCompare(humanize(b)));

    return {
      kept: keptRows,
      folded: foldedRows,
      organizationLabels: needsFolding
        ? [...namedLabels, OTHER_ORGANIZATION_LABEL]
        : namedLabels,
    };
  }, [allOrganizations]);

  const colorFor = React.useCallback(
    (name: string) => {
      if (name === OTHER_ORGANIZATION_LABEL) return OTHER_ORGANIZATION_COLOR;
      const index = organizationLabels.indexOf(name);
      return ORGANIZATION_COLORS[index % ORGANIZATION_COLORS.length];
    },
    [organizationLabels],
  );

  // One row per rating (the fixed scale, worst to best) — each organization's OWN
  // percentage for that rating becomes a segment, so a row compares organizations the
  // same way a Department-chart row compares departments: independent scores placed
  // side by side, not a literal share of one shared total.
  const rows = React.useMemo(() => {
    const grandTotal = allOrganizations.reduce(
      (sum, row) => sum + row.totalResponses,
      0,
    );

    return ratings.map((rating) => {
      const cells: Record<string, number> = {};

      kept.forEach((row) => {
        cells[row.organization] = row.percentages?.[rating] ?? 0;
      });

      if (folded.length > 0) {
        const foldedCounts = folded.reduce(
          (sum, row) => sum + (row.counts?.[rating] || 0),
          0,
        );
        const foldedTotal = folded.reduce(
          (sum, row) => sum + row.totalResponses,
          0,
        );
        cells[OTHER_ORGANIZATION_LABEL] =
          foldedTotal > 0 ? Math.round((foldedCounts / foldedTotal) * 100) : 0;
      }

      const totalForRating = allOrganizations.reduce(
        (sum, row) => sum + (row.counts?.[rating] || 0),
        0,
      );

      const summary: RatingSummary = {
        rating,
        totalResponses: totalForRating,
        sharePercent:
          grandTotal > 0 ? Math.round((totalForRating / grandTotal) * 100) : 0,
      };

      return { rating, _row: summary, ...cells };
    });
  }, [ratings, kept, folded, allOrganizations]);

  const summary = React.useMemo(() => {
    const total = allOrganizations.reduce(
      (sum, row) => sum + row.totalResponses,
      0,
    );
    if (total === 0) return null;
    const positive = allOrganizations.reduce(
      (sum, row) => sum + row.counts.Good + row.counts["Very Good"],
      0,
    );
    return {
      total,
      positive: Math.round((positive / total) * 100),
    };
  }, [allOrganizations]);

  const hasData = rows.length > 0 && organizationLabels.length > 0;
  const chartHeight = Math.max(320, rows.length * 68 + 90);
  const yAxisWidth = yAxisWidthFor(rows.map((row) => row.rating));

  return (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "20px",
        border: "1px solid #e0e0e0",
        overflow: "hidden",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      {/* header */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #e0e0e0",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: colors.bgPrimary,
        }}
      >
        <div style={{ padding: "1.25rem 2rem" }}>
          <h3 style={{ margin: 0, fontSize: "1.25rem", color: "#e0e0e0" }}>
            Satisfaction by Organization
          </h3>
          <p
            style={{
              margin: "0.25rem 0 0 0",
              fontSize: "0.875rem",
              color: "#e0e0e0",
            }}
          >
            {summary
              ? `${organizationLabels.length} organizations • ${summary.total.toLocaleString()} responses • ${
                  summary.positive
                }% rated Good or Very Good`
              : "How each organization's responses split across ratings, worst to best"}
          </p>
        </div>
      </div>

      {/* graph */}
      <div
        style={{
          paddingLeft: "1rem",
          paddingRight: "2rem",
          paddingTop: "2rem",
          paddingBottom: "1rem",
        }}
      >
        {isLoading || isSyncing ? (
          <ChartLoading
            isSyncing={Boolean(isSyncing)}
            label="Loading satisfaction by organization…"
          />
        ) : syncEnded ? (
          <ChartSyncNotice
            state={sync.state as "failed" | "stalled"}
            message={sync.message}
            onRetry={retrySync}
          />
        ) : hasData ? (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              data={rows}
              layout="vertical"
              stackOffset="expand"
              barCategoryGap="28%"
              margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
            >
              <CartesianGrid
                horizontal={false}
                strokeDasharray="3 3"
                stroke={GRID_STROKE}
              />
              <XAxis
                type="number"
                domain={[0, 1]}
                ticks={[0, 0.25, 0.5, 0.75, 1]}
                tickFormatter={(value: number) => `${Math.round(value * 100)}%`}
                tick={{ fontSize: 12, fill: TEXT_SECONDARY }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="rating"
                width={yAxisWidth}
                tick={{ fontSize: 12, fill: TEXT_SECONDARY }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={<RatingTooltip />}
                cursor={{ fill: "rgba(15,23,42,0.04)" }}
              />
              <Legend
                verticalAlign="bottom"
                align="center"
                iconType="square"
                iconSize={10}
                // Recharts defaults to sorting the legend alphabetically, which would
                // separate "Other organizations" from the end of the list. Keep the
                // same order the segments and colours use instead.
                itemSorter={(item: any) =>
                  organizationLabels.indexOf(item.value)
                }
                wrapperStyle={{
                  paddingTop: "1rem",
                  fontSize: "0.8rem",
                  color: TEXT_SECONDARY,
                }}
              />
              {organizationLabels.map((label) => (
                <Bar
                  key={label}
                  dataKey={label}
                  name={humanize(label)}
                  stackId="organization"
                  fill={colorFor(label)}
                  stroke="#ffffff"
                  strokeWidth={2}
                >
                  <LabelList
                    dataKey={label}
                    content={makeSegmentLabel(
                      colorFor(label),
                      (value) => `${value}%`,
                    )}
                  />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "2rem",
              color: "#666",
            }}
          >
            No ratings available for this survey and date range
          </div>
        )}
      </div>
    </div>
  );
}

export default OrganizationSatisfaction;
